import React, { Component } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get, isEqual } from 'lodash';

import asyncRoute from '../helpers/hoc/asyncRoute';

// routes import
import authenticationRoutes from '../routes/authentication';
import fudrRoutes from '../routes/fudr';
import restaurantRoutes from '../routes/restaurant';
import restaurantGroupRoutes from '../routes/restaurantGroup';
import { appName, serverErrorCodes } from '../constants/globals';
import rootStore from '../store';

import {
  clearApiErrorResponseAction,
  fetchMasterDataAction,
  markPersistorForPurgeAction,
} from '../actions/common';
import { clearSignInDataAction } from 'actions';
import { getIsApiRequestOnHold, setIsApiRequestOnHold } from 'utils/helpers';

import '../global/styles/common.scss';
import warningIcon from '../assets/images/icons/warning.svg';
import cancelIcon from '../assets/images/icons/cancel.svg';

// containers import

const Authentication = asyncRoute(() => import('./authentication'));
const Fudr = asyncRoute(() => import('./fudr'));
const Restaurant = asyncRoute(() => import('./restaurant'));
const RestaurantGroup = asyncRoute(() => import('./restaurantGroup'));
const ErrorPage = asyncRoute(() => import('../components/errorPage'));

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lockedUserId: null,
      lockedErrorResponse: null,
    };
  }

  componentDidMount() {
    this.props.actions.fetchMasterData({ appName }); // fetch master Data on App mount
  }

  componentDidUpdate(prevProps) {
    if (
      !isEqual(prevProps?.persistorMarkedForPurge, this.props?.persistorMarkedForPurge) &&
      this.props?.persistorMarkedForPurge
    ) {
      this.clearSessionAndRedirect();
    }
    if (!isEqual(prevProps.errorResponse, this.props.errorResponse)) {
      if (this.props.errorResponse?.code === serverErrorCodes?.invalidToken) {
        if (this.state.lockedErrorResponse === null) {
          this.setState({ lockedErrorResponse: this.props.errorResponse });
        }

        if (this.state.lockedUserId === null) {
          const userId = get(this.props.authData, 'user.name', '');
          this.setState({ lockedUserId: userId });
        }

        const prefilledData = {
          userId: this.state.lockedUserId,
          errorResponse: this.state.lockedErrorResponse,
        };
        this.props?.actions?.clearApiErrorResponse();
        this.handleSessionExpiredErrorResponse({ prefilledData });
      }
    }
  }

  handleSessionExpiredErrorResponse = ({ prefilledData }) => {
    const { authData, history } = this.props;
    const signedInUsername = get(authData, 'user.name', '');
    const accessToken = get(authData, 'accessToken', '');
    this.props.actions.clearSignInData({
      history,
      signedInUsername,
      isPseudoSignIn: this.props.isPseudoSignIn,
      prefilledData,
      accessToken,
    });
    setIsApiRequestOnHold(false);
  };

  clearSessionAndRedirect = () => {
    const { persistor } = rootStore;
    // Clear the store being persisted from session storage
    persistor
      .purge()
      .then(() => {
        this.props.actions.markPersistorForPurge(false).then(() => {
          window.location.replace(window.location.origin);
        });
      })
      .catch((error) => {
        console.error('There was an error purging the store', error);
      });
  };

  render() {
    const { match } = this.props;
    return (
      <>
        <Switch>
          <Route
            exact
            path={`${match.path}`}
            render={() => <Redirect to={`${match.path}/${authenticationRoutes.authentication}`} />}
          />
          <Route
            path={`${match.path}/${authenticationRoutes.authentication}`}
            component={Authentication}
          />
          <Route path={`${match.path}/${fudrRoutes.fudr}`} component={Fudr} />
          <Route path={`${match.path}/${restaurantRoutes.restaurant}`} component={Restaurant} />
          <Route
            path={`${match.path}/${restaurantGroupRoutes.restaurantGroup}`}
            component={RestaurantGroup}
          />
          <Route component={ErrorPage} />
        </Switch>
        {/* Load any resources needed to be cached in case user agent goes offline */}
        <img src={warningIcon} style={{ display: 'none' }} alt="exception" />
        <img src={cancelIcon} style={{ display: 'none' }} alt="cancel" />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  persistorMarkedForPurge: state?.common?.persistorMarkedForPurge ?? false,
  errorResponse: state?.common?.errorResponse ?? {},
  isPseudoSignIn: state?.authentication?.isPseudoSignIn ?? false,
  authData: state?.authentication?.authData ?? '',
  authentication: state?.authentication ?? {},
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchMasterData: fetchMasterDataAction,
      markPersistorForPurge: markPersistorForPurgeAction,
      clearSignInData: clearSignInDataAction,
      clearApiErrorResponse: clearApiErrorResponseAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
